import React from 'react';
import { IonIcon } from '@ionic/react';
import { IonicButton } from 'ionic-ui';
import { add, walletOutline, people, person } from 'ionicons/icons';
import { useDialog } from '../../../../context/Dialog';
import WalletKycDialog from './dialogs/walletKycDialogs/WalletKycDialog';
import WalletIndividualKycDialog from './dialogs/walletIndividualDialogs/WalletIndividualKycDialog';
import VerificationMethodSelector from '../../../auth-components/signup/VerificationMethodSelector';
import { WalletSetupResume } from '../../../../types/wallet/Walletkyc';

type Props = {
  setupResume?: WalletSetupResume | null;
};

const CORPORATE_WALLET_SETUP_AVAILABLE = false;

const WalletSetupCard: React.FC<Props> = ({ setupResume }) => {
  const { openDialog } = useDialog();
  const handleCorporateWalletSetup = () =>
    openDialog(
      <WalletKycDialog
        setupResume={
          setupResume?.entity_type == 'corporate' ? setupResume : undefined
        }
      />,
      {
        allowBackgroundDismiss: false,
      }
    );

  const handleIndividualWalletSetup = () =>
    openDialog(
      <WalletIndividualKycDialog
        setupResume={
          setupResume?.entity_type == 'individual' ? setupResume : undefined
        }
      />,
      {
        allowBackgroundDismiss: false,
      }
    );

  return (
    <div className="p-4 sm:p-6 min-h-56 h-full bg-background dark:bg-background-accent border border-border rounded-lg overflow-hidden">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-text-dark-high dark:text-text-light-high">
          My Wallet
        </h2>
        <IonicButton
          variant="ghost"
          disabled
          className="min-w-8 min-h-8 w-8 h-8 flex items-center justify-center"
        >
          <IonIcon
            icon={add}
            size="20px"
            className="text-text-dark-high dark:text-text-light-high"
          />
        </IonicButton>
      </div>
      <div className="before:w-[calc(100%-2rem)] before:top-4 mb-8 before:-translate-x-1/2 before:left-1/2 before:h-full before:absolute relative before:rounded-lg before:z-[1] before:bg-customs-lavender-foreground before:dark:bg-background-accent before:border before:border-secondary">
        <div className="p-4 bg-customs-lavender-foreground dark:bg-background-accent border border-secondary rounded-lg relative z-[3] space-y-4">
          <div className="bg-white p-2 rounded-lg shadow-sm border border-secondary text-primary flex items-center justify-center w-fit mx-auto">
            <IonIcon icon={walletOutline} className="size-12" />
          </div>
          <div className="flex flex-col justify-between gap-1 items-center mb-3">
            <h3 className="text-lg font-semibold high-emphasis-text">
              {setupResume ? 'Complete wallet setup' : 'Set up your wallet'}
            </h3>
            <p className="text-center text-sm medium-emphasis-text">
              {setupResume
                ? 'Some required corporate KYC tasks are still outstanding. Continue setup and we will take you to the next required step automatically.'
                : 'Create a secure wallet to store funds for in-app purchases, top-ups, and transfers. It only takes a moment to get started.'}
            </p>
          </div>

          {/* ACTION BUTTONS */}
          <div className="space-y-2">
            {CORPORATE_WALLET_SETUP_AVAILABLE && (
              <div className="mt-4 rounded-lg border border-blue-100 bg-blue-50 p-3">
                <div className="flex gap-2">
                  {/* Simple Info Icon */}
                  <svg
                    className="h-5 w-5 text-primary shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="high-emphasis-text">
                    <small className="font-semibold uppercase">
                      Full corporate setup requirements:{' '}
                    </small>
                    <small className="mt-1">
                      To be approved, you must provide your{' '}
                      <span className="font-semibold text-primary">
                        TIN, CAC, Memorandum and Articles, utility bill, and
                        SCUML
                      </span>
                      . At least one director must be registered.
                    </small>
                  </div>
                </div>
              </div>
            )}
            {CORPORATE_WALLET_SETUP_AVAILABLE && (
              <VerificationMethodSelector
                icon={people}
                className="py-4 px-2 w-fit"
                size="fit"
                title={
                  setupResume && setupResume.entity_type == 'corporate'
                    ? 'Continue Corporate Setup'
                    : 'Full Corporate Setup'
                }
                // subtitle="Best if you have all official documents ready to upload now."
                onClick={handleCorporateWalletSetup}
              />
            )}
            <VerificationMethodSelector
              icon={person}
              className="py-4 px-2 w-fit h-fit !min-h-fit"
              title={
                setupResume && setupResume.entity_type == 'individual'
                  ? 'Continue wallet setup'
                  : 'Simple business wallet setup'
              }
              onClick={handleIndividualWalletSetup}
              size="fit"
              // subtitle="Use this if you,re still getting your official paperwork in order."
            />
            {/* <button
              className="flex-1 disabled:hover:cursor-default flex items-center justify-center gap-2 py-2 bg-white border border-gray-300 rounded-lg text-sm text-text-dark-high font-medium hover:bg-gray-50 hover:cursor-pointer transition"
              onClick={handleWalletSetup}
            >
              <span>Get Started</span>
              <IonIcon icon={enterOutline} className="text-inherit" />
            </button> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletSetupCard;
